import { useEffect } from 'react';

import { Pdf01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import PropTypes from 'prop-types';

export const Download = ({
  title,
  description,
  downloadUrl,
  fileName,
  className,
  disabled = false,
  onClick = () => {},
  iconSize = '18',
  showIcon = true,
  ariaLabel,
  ariaDescribedBy,
  role = 'button',
  tabIndex = 0,
  enableKeyboardShortcut = true,
  ...props
}) => {
  const handleClick = e => {
    if (disabled) return;

    if (onClick) {
      onClick(e);
    }

    if (downloadUrl) {
      // Create a temporary anchor element to open in new tab
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Generate unique IDs for accessibility
  const titleId = title ? `download-title-${Math.random().toString(36).substr(2, 9)}` : null;
  const descriptionId = description
    ? `download-description-${Math.random().toString(36).substr(2, 9)}`
    : null;

  // Create accessible label
  const accessibleLabel =
    ariaLabel ||
    (fileName ? `Download ${fileName}` : 'Download file') + (disabled ? ' (disabled)' : '');

  return (
    <div
      className={`d-flex flex-column u-gap-medium u-border u-border-radius u-border-radius--medium u-padding ${className || ''}`}
      role="region"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      {...props}>
      {title && (
        <span id={titleId} className="primary-text u-font-size-x-large u-font-weight-500">
          {title}
        </span>
      )}
      {description && (
        <span id={descriptionId} className="secondary-text u-font-size-medium u-font-weight-400">
          {description}
        </span>
      )}
      <a
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-label={accessibleLabel}
        aria-describedby={ariaDescribedBy || (descriptionId ? descriptionId : undefined)}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : tabIndex}
        className={`u-link u-font-size-small d-flex u-gap-small align-items-center u-pointer ${disabled ? 'disabled' : ''}`}>
        {showIcon && (
          <HugeiconsIcon size={iconSize} icon={Pdf01Icon} strokeWidth="1.5" aria-hidden="true" />
        )}
        <span className="u-link" aria-hidden="false">
          {fileName || 'Click here to download the file'}
        </span>
      </a>
    </div>
  );
};

Download.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  downloadUrl: PropTypes.string,
  fileName: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  iconSize: PropTypes.oneOf(['x-small', 'small', 'medium', 'large', 'xlarge', 'xxlarge']),
  showIcon: PropTypes.bool,
  // Accessibility props
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  role: PropTypes.string,
  tabIndex: PropTypes.number,
  // Keyboard shortcut props
  enableKeyboardShortcut: PropTypes.bool,
};
